<!--
  @project E-School Web
  @author Antigravity AI
  @date 2026-08-24
-->

<script lang="ts" setup>
import { ref } from 'vue';
import { useClipboard } from '@vueuse/core';
import imgLogo from '~/assets/images/logo-full.png';

const { t, locale } = useI18n();
const toast = useToast();
const { copy } = useClipboard({ legacy: true });

// Page SEO Meta
useSeoMeta({
  title: () => t('support.seo.title'),
  description: () => t('support.seo.desc'),
  robots: 'noindex, nofollow',
});

// Resource items definition
const resources = ref([
  {
    id: 'khmer-fonts',
    titleEn: 'Install Khmer Fonts (Linux / ChromeOS Flex)',
    titleKh: 'ដំឡើងពុម្ពអក្សរខ្មែរ (លីនុច និង ក្រូមអូអេស ផ្លិច)',
    descEn: 'Fixes Khmer names showing as square boxes (tofu) in the app by installing Khmer OS core fonts.',
    descKh:
      'ដោះស្រាយបញ្ហាឈ្មោះអក្សរខ្មែរដែលបង្ហាញចេញជាសញ្ញាប្រអប់ការ៉េនៅក្នុងកម្មវិធី ដោយដំឡើងពុម្ពអក្សរខ្មែរជាមូលដ្ឋាន។',
    command: 'sudo apt-get update && sudo apt-get install -y fonts-khmeros fonts-khmeros-core',
    os: 'linux',
  },
  {
    id: 'linux-installer',
    titleEn: 'Install E-School Manager (Linux / ChromeOS Flex)',
    titleKh: 'ដំឡើងកម្មវិធីគ្រប់គ្រង E-School (លីនុច និង ក្រូមអូអេស ផ្លិច)',
    descEn: 'Quick one-liner command to install or upgrade the E-School Cambodia Manager tool.',
    descKh: 'ពាក្យបញ្ជាដំឡើង ឬធ្វើបច្ចុប្បន្នភាពកម្មវិធី E-School Cambodia Manager យ៉ាងរហ័ស។',
    command: 'curl -sL https://e-schoolcambodia.com/download/linux/install.sh | bash',
    os: 'linux',
  },
]);

const copiedId = ref<string | null>(null);

const handleCopy = async (id: string, text: string) => {
  try {
    await copy(text);
    copiedId.value = id;
    toast.add({
      title: t('support.card.copySuccess'),
      description: t('support.card.copySuccessDesc'),
      color: 'success',
    });
    setTimeout(() => {
      if (copiedId.value === id) {
        copiedId.value = null;
      }
    }, 2000);
  } catch {
    toast.add({
      title: t('support.card.copyFailed'),
      description: t('support.card.copyFailedDesc'),
      color: 'error',
    });
  }
};
</script>

<template>
  <div
    class="flex min-h-screen flex-col bg-slate-50 font-sans text-slate-800 selection:bg-brand-red selection:text-white dark:bg-slate-900 dark:text-slate-100"
  >
    <!-- Simple Navigate Header -->
    <nav
      class="sticky top-0 z-50 border-b border-slate-200 bg-white/85 px-6 py-4 backdrop-blur-md dark:border-slate-800 dark:bg-slate-950/85"
    >
      <div class="mx-auto flex max-w-5xl items-center justify-between">
        <NuxtLink to="/" class="flex items-center gap-3">
          <img :src="imgLogo" :alt="t('support.navigation.brand')" class="h-8 object-contain" />
        </NuxtLink>
        <NuxtLink
          to="/"
          class="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
          <Icon name="lucide:arrow-left" class="h-3.5 w-3.5" />
          <span>{{ t('support.navigation.backToHome') }}</span>
        </NuxtLink>
      </div>
    </nav>

    <!-- Hero Header -->
    <header class="bg-linear-to-br from-brand-blue via-[#056a97] to-[#034a68] py-12 text-center text-white md:py-16">
      <div class="mx-auto max-w-4xl px-6">
        <h1 class="text-3xl font-extrabold md:text-4xl">{{ t('support.hero.title') }}</h1>
        <h2 class="font-kantumruy mt-1 text-xl font-medium text-white/90">{{ t('support.hero.subtitle') }}</h2>
        <p class="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/75 md:text-base">
          {{ t('support.hero.desc') }}
        </p>
      </div>
    </header>

    <!-- Main Content -->
    <main class="mx-auto w-full max-w-5xl grow px-6 py-12 md:py-16">
      <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div
          v-for="res in resources"
          :key="res.id"
          class="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-950"
        >
          <!-- Title & Platform Badge -->
          <div class="mb-4 flex items-start justify-between">
            <div>
              <h3 class="text-left text-lg font-bold text-slate-900 dark:text-slate-50">
                {{ locale === 'kh' ? res.titleKh : res.titleEn }}
              </h3>
            </div>
            <span
              class="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-900 dark:text-slate-400"
            >
              {{ res.os.toUpperCase() }}
            </span>
          </div>

          <!-- Description -->
          <p class="text-left text-xs leading-relaxed text-slate-600 dark:text-slate-400">
            {{ locale === 'kh' ? res.descKh : res.descEn }}
          </p>

          <!-- Terminal Block -->
          <div class="mt-6 rounded-xl bg-slate-900 p-4 font-mono text-xs text-slate-300 dark:bg-slate-950">
            <div class="mb-3 flex items-center justify-between border-b border-slate-800 pb-2">
              <span class="text-[10px] tracking-wider text-slate-500 uppercase">{{
                t('support.card.terminalCommand')
              }}</span>
              <button
                class="flex items-center gap-1 rounded bg-slate-800 px-2.5 py-1 text-[11px] font-semibold text-white transition-all hover:bg-slate-700 active:scale-95"
                @click="handleCopy(res.id, res.command)"
              >
                <Icon
                  :name="copiedId === res.id ? 'lucide:check' : 'lucide:copy'"
                  class="h-3.5 w-3.5"
                  :class="copiedId === res.id ? 'text-emerald-400' : 'text-slate-400'"
                />
                <span>{{ copiedId === res.id ? t('support.card.copied') : t('support.card.copy') }}</span>
              </button>
            </div>
            <code class="block text-left break-all whitespace-pre-wrap text-emerald-400 select-all">{{
              res.command
            }}</code>
          </div>
        </div>
      </div>
    </main>

    <!-- Simple Copyright Footer -->
    <footer
      class="border-t border-slate-200 bg-white py-6 text-center text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400"
    >
      <div class="mx-auto max-w-5xl px-6">
        <p>&copy; {{ new Date().getFullYear() }} E-School Cambodia. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>
